import { useCallback } from "react";
import {
  NOTIFICATION_STATUSES,
  useNotifications,
} from "@adsk/alloy-react-notification";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";
import { updateIssueByIdAction } from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import {
  createCustomEvent,
  useFireCustomEvent,
} from "@adsk/acc-issues-core/providers/CustomEventContext";
import { useTranslations } from "@adsk/acc-issues-translations";
import {
  CustomEventType,
  Issue,
  IssueAttributes,
} from "@adsk/acc-issues-types";
import { trackIssueBaseFieldEdit } from "../utils/analytics/issueActionsTracking";
import type { UpdateOrigin } from "../utils/analytics/issueActionsTracking";

interface UpdateIssueFnParams {
  issueId: string;
  issueChanges: Partial<IssueAttributes>;
  onSuccess?: (updatedIssue: Issue) => void;
  onFailure?: () => void;
  analyticsData?: {
    origin?: UpdateOrigin;
  };
}

type UpdateIssueFnType = (params: UpdateIssueFnParams) => Promise<unknown>;

export const useUpdateIssue = (): UpdateIssueFnType => {
  const dispatch = useDispatch();
  const { showNotification } = useNotifications();
  const { T } = useTranslations();
  const broadcastCustomEvent = useFireCustomEvent();

  return useCallback(
    async ({
      issueId,
      issueChanges,
      onSuccess,
      onFailure,
      analyticsData = {},
    }: UpdateIssueFnParams) => {
      const { origin } = analyticsData;

      return dispatch(
        updateIssueByIdAction({
          id: issueId,
          issueChanges,
          onSuccess: (updatedIssue: Issue) => {
            broadcastCustomEvent?.(
              createCustomEvent({
                type: CustomEventType.IssueModified,
                target: null,
                data: { issueId, issueChanges },
              })
            );
            trackIssueBaseFieldEdit({
              issue: updatedIssue,
              issueChanges,
              analyticsData: { origin },
            });
            onSuccess?.(updatedIssue);
          },
          onFailure: () => {
            showNotification({
              status: NOTIFICATION_STATUSES.ERROR,
              message: T.translate("editable.failureMessage"),
            });
            onFailure?.();
          },
        })
      );
    },
    [dispatch, showNotification, T, broadcastCustomEvent]
  );
};
